import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import Loading from './ui/Loading';

interface PermissionProtectedRouteProps {
  children: React.ReactNode;
  /** Screen code required to access this route */
  screenCode: string;
  /** Redirect path if permission is denied (defaults to /dashboard) */
  unauthorizedRedirect?: string;
}

/**
 * PermissionProtectedRoute - Protects routes with both authentication and permission checks
 * 
 * @example
 * <PermissionProtectedRoute screenCode="CAMERAS">
 *   <CamerasPage />
 * </PermissionProtectedRoute>
 */
const PermissionProtectedRoute: React.FC<PermissionProtectedRouteProps> = ({ 
  children, 
  screenCode,
  unauthorizedRedirect = '/dashboard'
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { canAccessScreen, locationLoading } = usePermissions();
  const location = useLocation();

  if (isLoading || locationLoading) {
    return <Loading fullScreen text="Loading..." />;
  }

  if (!isAuthenticated) {
    // Redirect to login page but save the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has permission to view this screen
  if (!canAccessScreen(screenCode)) {
    // Redirect to unauthorized page or dashboard
    return <Navigate to={unauthorizedRedirect} replace />;
  }

  return <>{children}</>;
};

export default PermissionProtectedRoute;
